// ==========================================================================
// lib/auth.tsx — who is logged in, and have they passed MFA (FF-003, 9 Oct 2026)
//
// The app's rule: no MFA, no data. The database enforces it (every policy
// checks the JWT's aal claim = aal2). These helpers only decide which screen
// to show:
//   signed out                    -> Login
//   signed in, no MFA factor yet  -> MfaSetup   (first time only)
//   signed in, MFA not done yet   -> MfaVerify
//   signed in, aal2               -> the app; claim_membership() links the
//                                    login to the household row seeded for
//                                    that email
// ==========================================================================
import { Fragment, createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { useQueryClient } from "@tanstack/react-query";
import { authRevision, clearSessionCache } from "./queryPrivacy";
import { readOnlyPreview } from "./previewSafety";

export type AuthStage = "loading" | "signed-out" | "mfa-setup" | "mfa-verify" | "ready";

/** Why the sign-in screen is showing, when it wasn't the person's own Sign out (FF-061). */
export const signedOutWhy = () => { try { return sessionStorage.getItem("signed_out_why") ?? ""; } catch { return ""; } };
const noteSignedOut = (why: string) => { try { if (why) sessionStorage.setItem("signed_out_why", why); else sessionStorage.removeItem("signed_out_why"); } catch { /* fine */ } };

type AuthState = {
  stage: AuthStage;
  session: Session | null;
  householdIds: string[];
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

async function workOutStage(session: Session | null): Promise<{ stage: AuthStage; householdIds: string[] }> {
  if (!session) return { stage: "signed-out", householdIds: [] };
  const { data: aal, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalErr || !aal) return { stage: "signed-out", householdIds: [] };
  if (aal.currentLevel === "aal2") {
    noteSignedOut("");
    if (readOnlyPreview) {
      const { data, error } = await supabase.from("household_memberships").select("household_id").eq("auth_user_id", session.user.id).is("revoked_at", null);
      if (error) throw error;
      return { stage: "ready", householdIds: (data ?? []).map((m) => m.household_id as string) };
    }
    const { data, error } = await supabase.rpc("claim_membership");
    if (error) throw error;
    return { stage: "ready", householdIds: (data as string[] | null) ?? [] };
  }
  // aal1: do they have a verified factor to challenge, or do they need to set one up?
  const { data: factors } = await supabase.auth.mfa.listFactors();
  const verified = (factors?.totp ?? []).filter((f) => f.status === "verified");
  return { stage: verified.length > 0 ? "mfa-verify" : "mfa-setup", householdIds: [] };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const revision = useRef(authRevision());
  const user = useRef<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [stage, setStage] = useState<AuthStage>("loading");
  const [householdIds, setHouseholdIds] = useState<string[]>([]);

  const byUs = useRef(false);
  const resolve = useCallback(async (next: Session | null, ticket: number) => {
    if (!revision.current.current(ticket)) return;
    try {
      const r = await workOutStage(next);
      if (!revision.current.current(ticket)) return;
      if (r.stage !== "ready") clearSessionCache(queryClient);
      setSession(next); setStage(r.stage); setHouseholdIds(r.householdIds);
    } catch {
      if (!revision.current.current(ticket)) return;
      clearSessionCache(queryClient);
      setSession(null); setStage("signed-out"); setHouseholdIds([]);
      noteSignedOut("We couldn't verify your access. Please sign in again.");
    }
  }, [queryClient]);

  const begin = useCallback((next: Session | null) => {
    const ticket = revision.current.next();
    if (user.current !== (next?.user.id ?? null) || !next) {
      user.current = next?.user.id ?? null;
      setStage("loading"); setHouseholdIds([]); setSession(null);
      clearSessionCache(queryClient);
    }
    return ticket;
  }, [queryClient]);

  const refresh = useCallback(async () => {
    const ticket = revision.current.next();
    const { data } = await supabase.auth.getSession();
    if (!revision.current.current(ticket)) return;
    await resolve(data.session, begin(data.session));
  }, [begin, resolve]);

  useEffect(() => {
    void refresh();
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      // A sign-out we didn't ask for means the saved session was lost (expired, refused by the server, or storage cleared).
      if (event === "SIGNED_OUT" && !byUs.current) noteSignedOut("You were signed out, so the app needs your password and code again.");
      const ticket = begin(next);
      // Leave Supabase's auth lock before asking its MFA APIs for the stage.
      setTimeout(() => { void resolve(next, ticket); }, 0);
    });
    return () => { sub.subscription.unsubscribe(); revision.current.next(); };
  }, [refresh, begin, resolve]);

  const signOut = useCallback(async () => {
    byUs.current = true; noteSignedOut("");
    begin(null);
    setSession(null);
    setStage("signed-out");
    setHouseholdIds([]);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) noteSignedOut("Sign-out could not reach the server. Please try again before sharing this device.");
    } finally { byUs.current = false; }
  }, [begin]);

  return (
    <AuthContext.Provider value={{ stage, session, householdIds, refresh, signOut }}>
      <Fragment key={session?.user.id ?? "signed-out"}>{children}</Fragment>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
